/**
 * business_investment.ts — 312 Solutions LLC expansion investment model.
 *
 * Supports two loan modes:
 *
 *   'amortizing': LLC repays P+I over loanTermYears.
 *     - Interest portion taxable; principal return is tax-free.
 *     - After payoff, postLoanYieldRate% of original principal annually.
 *
 *   'interest-only': LLC pays interest-only annually; principal held in LLC
 *     (counted as a net worth asset — business loan receivable).
 *     - Interest income taxable each year.
 *     - Profit distribution (postLoanYieldRate%) paid alongside interest.
 *     - Principal returned as lump sum at exitYearOffset (non-taxable).
 *     - No market growth on outstanding principal (fixed-rate note, not equities).
 *
 * Tax treatment: passive income — ordinary income brackets, NO SE tax.
 *
 * When expansion is enabled (interest-only mode), the existing $200k building
 * is treated as the original investment round already deployed at simulation start.
 * Its interest + profit share replaces BUSINESS_INCOME_ANNUAL in montecarlo.ts,
 * and its principal returns as a non-taxable lump sum at exitYearOffset.
 *
 * See: docs/methodology/features/2026-06-06-312-solutions-scenario/brief.md
 */

import type { BusinessInvestmentParams } from './types'
import { EXISTING_BUSINESS_LOAN_PRINCIPAL } from './defaults'

// ─── Amortizing loan helpers ──────────────────────────────────────────────────

export function annualLoanPayment(
  principal: number,
  annualRate: number,
  termYears: number,
): number {
  if (principal <= 0 || termYears <= 0) return 0
  if (annualRate === 0) return principal / termYears
  const r = annualRate
  const n = termYears
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
}

export function loanBalanceAtYear(
  principal: number,
  annualRate: number,
  termYears: number,
  yearsPaid: number,
): number {
  if (yearsPaid >= termYears || principal <= 0) return 0
  if (annualRate === 0) return principal * (1 - yearsPaid / termYears)
  const r = annualRate
  const n = termYears
  return (
    (principal * (Math.pow(1 + r, n) - Math.pow(1 + r, yearsPaid))) /
    (Math.pow(1 + r, n) - 1)
  )
}

// ─── Result shape ─────────────────────────────────────────────────────────────

export interface BizInvestmentResult {
  /** Cash income received this year (interest, P+I payments, or yield distributions) */
  income: number
  /** Capital deployed to LLC this year (reduces liquid at deployment) */
  capitalDeployed: number
  /** Taxable portion of income (no SE tax; ordinary income brackets only) */
  taxablePassiveIncome: number
  /** Principal returned as lump sum this year (non-taxable; goes to liquid) */
  principalReturned: number
  /** Total principal still outstanding in the LLC across all rounds (net worth asset) */
  outstandingPrincipal: number
}

// ─── Main computation ─────────────────────────────────────────────────────────

export function compute312Expansion(
  params: BusinessInvestmentParams,
  yearOffset: number,
  inflationFactor: number,
): BizInvestmentResult {
  if (!params.enabled || params.maxRounds <= 0) {
    return {
      income: 0,
      capitalDeployed: 0,
      taxablePassiveIncome: 0,
      principalReturned: 0,
      outstandingPrincipal: 0,
    }
  }

  let income = 0
  let capitalDeployed = 0
  let taxablePassiveIncome = 0
  let principalReturned = 0
  let outstandingPrincipal = 0

  // ── Existing $200k building (already deployed at simulation start) ────────
  // In interest-only mode only: this replaces BUSINESS_INCOME_ANNUAL in the model.
  // Same loan rate, profit share, and exit year as expansion rounds.
  // Amortizing mode keeps the existing building in BUSINESS_INCOME_ANNUAL unchanged.
  if (params.loanMode === 'interest-only') {
    if (yearOffset === params.exitYearOffset) {
      // Property sells — principal returned to liquid (non-taxable)
      principalReturned += EXISTING_BUSINESS_LOAN_PRINCIPAL
    } else if (yearOffset < params.exitYearOffset) {
      // Still active — collect interest + profit share
      outstandingPrincipal += EXISTING_BUSINESS_LOAN_PRINCIPAL
      const existingInterest = EXISTING_BUSINESS_LOAN_PRINCIPAL * params.loanRate
      const existingProfit   = EXISTING_BUSINESS_LOAN_PRINCIPAL * params.postLoanYieldRate * inflationFactor
      income += existingInterest + existingProfit
      taxablePassiveIncome += existingInterest + existingProfit
    }
    // After exit year: building sold, no further income or receivable from it
  }

  for (let round = 0; round < params.maxRounds; round++) {
    const deployYearOffset = round * params.roundFrequency
    if (deployYearOffset > yearOffset) break

    const yearsActive = yearOffset - deployYearOffset  // 0 = deployment year

    if (yearsActive === 0) {
      // Deployment year — capital leaves liquid, nothing returned yet
      capitalDeployed += params.roundAmount

    } else if (params.loanMode === 'interest-only') {
      // ── Interest-only mode ────────────────────────────────────────────────
      const roundExitOffset = deployYearOffset + params.exitYearOffset

      if (yearOffset === roundExitOffset) {
        // Property sells — full principal returned as lump sum (non-taxable)
        principalReturned += params.roundAmount
        // Interest still accrues in the sale year (partial year not modeled — conservative)
      } else if (yearOffset < roundExitOffset) {
        // Still holding — collect interest + profit distribution
        outstandingPrincipal += params.roundAmount

        const interestIncome = params.roundAmount * params.loanRate
        // Profit distribution: NOI share above the interest (inflation-adjusted)
        const profitDistribution = params.roundAmount * params.postLoanYieldRate * inflationFactor

        income += interestIncome + profitDistribution
        taxablePassiveIncome += interestIncome + profitDistribution
      }
      // After exit: principal is back in liquid; no further income from this round

    } else {
      // ── Amortizing mode ───────────────────────────────────────────────────
      if (yearsActive <= params.loanTermYears) {
        // Loan phase: P+I
        const annualPmt = annualLoanPayment(
          params.roundAmount,
          params.loanRate,
          params.loanTermYears,
        )
        const startBalance = loanBalanceAtYear(
          params.roundAmount,
          params.loanRate,
          params.loanTermYears,
          yearsActive - 1,
        )
        const interestThisYear = startBalance * params.loanRate
        outstandingPrincipal += Math.max(0, startBalance - (annualPmt - interestThisYear))

        income += annualPmt
        taxablePassiveIncome += interestThisYear
      } else {
        // Post-payoff yield phase
        const yearlyYield = params.roundAmount * params.postLoanYieldRate * inflationFactor
        income += yearlyYield
        taxablePassiveIncome += yearlyYield
      }
    }
  }

  return {
    income,
    capitalDeployed,
    taxablePassiveIncome,
    principalReturned,
    outstandingPrincipal,
  }
}

export function previewIncomeAtYear(
  params: BusinessInvestmentParams,
  yearOffset: number,
  inflationMean: number,
): number {
  const inflationFactor = Math.pow(1 + inflationMean, yearOffset)
  return compute312Expansion(params, yearOffset, inflationFactor).income
}

export function previewOutstandingAtYear(
  params: BusinessInvestmentParams,
  yearOffset: number,
): number {
  return compute312Expansion(params, yearOffset, 1).outstandingPrincipal
}

/**
 * Break-even profit share above loan interest that makes the LLC investment
 * match the opportunity cost of leaving capital in the portfolio.
 *
 * Over exitYears, the portfolio compounds: total return = (1+r)^N - 1.
 * The LLC pays simple interest + profitShare each year: total = (rate + share) * N.
 * Setting equal and solving for profitShare:
 *   profitShare = ((1 + portfolioReturn)^exitYears - 1) / exitYears - loanRate
 *
 * Returns the profitShare as a fraction (e.g. 0.04 = 4%).
 * A negative result means the loan interest already beats the portfolio.
 */
export function breakEvenProfitShare(
  loanRate: number,
  portfolioReturnMean: number,
  exitYears: number,
): number {
  if (exitYears <= 0) return 0
  const portfolioTotalReturn = Math.pow(1 + portfolioReturnMean, exitYears) - 1
  return portfolioTotalReturn / exitYears - loanRate
}
